import { Route, Routes } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { AuthProvider } from './context/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { RoleProtectedRoute } from './components/RoleProtectedRoute'
import AppLayout from './components/AppLayout'
import NotFound from './pages/NotFound'
import { Loading } from './components/ui/Loading'

const Login = lazy(() => import('./pages/Login'))
const Home = lazy(() => import('./pages/Home'))
const UploadAnalyze = lazy(() => import('./pages/UploadAnalyze'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Chat = lazy(() => import('./pages/Chat'))
const Plugins = lazy(() => import('./pages/Plugins'))
const Billing = lazy(() => import('./pages/Billing'))
const Users = lazy(() => import('./pages/Users'))
const Workspaces = lazy(() => import('./pages/Workspaces'))
const Integrations = lazy(() => import('./pages/Integrations'))
const Settings = lazy(() => import('./pages/Settings'))
const Audit = lazy(() => import('./pages/Audit'))
const ApiKeys = lazy(() => import('./pages/ApiKeys'))
const Diagnostics = lazy(() => import('./pages/Diagnostics'))
const FlagsAdmin = lazy(() => import('./pages/FlagsAdmin'))
const WhatsNew = lazy(() => import('./pages/WhatsNew'))
const SessionsAdmin = lazy(() => import('./pages/SessionsAdmin'))
const WebhooksTester = lazy(() => import('./pages/WebhooksTester'))
const RateUsage = lazy(() => import('./pages/RateUsage'))
const AdvancedSearch = lazy(() => import('./pages/AdvancedSearch'))
const Connectors = lazy(() => import('./pages/Connectors'))
const ClauseExplorer = lazy(() => import('./pages/ClauseExplorer'))
const Anomalies = lazy(() => import('./pages/Anomalies'))
const ModelAccuracy = lazy(() => import('./pages/ModelAccuracy'))
const ClauseDrift = lazy(() => import('./pages/ClauseDrift'))
const ClauseLibrary = lazy(() => import('./pages/ClauseLibrary'))
const ClauseTemplates = lazy(() => import('./pages/ClauseTemplates'))
const SavedViews = lazy(() => import('./pages/SavedViews'))
const DocumentsExplorer = lazy(() => import('./pages/DocumentsExplorer'))
const Forbidden = lazy(() => import('./pages/Forbidden'))
const DeveloperPortal = lazy(() => import('./pages/DeveloperPortal'))
const PartnerOnboarding = lazy(() => import('./pages/PartnerOnboarding'))
const Marketplace = lazy(() => import('./pages/Marketplace'))
const Feedback = lazy(() => import('./pages/Feedback'))
const Compare = lazy(() => import('./pages/Compare'))
const DemoMode = lazy(() => import('./pages/DemoMode'))
const PersonaPlaybooks = lazy(() => import('./pages/PersonaPlaybooks'))
const LearningCenter = lazy(() => import('./pages/LearningCenter'))
const CollectionIntelligence = lazy(() => import('./pages/CollectionIntelligence'))
const DocumentTimeline = lazy(() => import('./pages/DocumentTimeline'))
const DocumentCopilot = lazy(() => import('./pages/DocumentCopilot'))
const NegotiationChecklist = lazy(() => import('./pages/NegotiationChecklist'))
const WhatChangedIf = lazy(() => import('./pages/WhatChangedIf'))
const ContractSimulator = lazy(() => import('./pages/ContractSimulator'))

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<div className="container py-8"><Loading label="Loading…" /></div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<ProtectedRoute />}> 
          <Route element={<AppLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/upload" element={<UploadAnalyze />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/learning-center" element={<LearningCenter />} />
            <Route path="/collection-intelligence" element={<CollectionIntelligence />} />
            <Route path="/document-timeline" element={<DocumentTimeline />} />
            <Route path="/document-copilot" element={<DocumentCopilot />} />
            <Route path="/negotiation-checklist" element={<NegotiationChecklist />} />
            <Route path="/what-changed-if" element={<WhatChangedIf />} />
            <Route path="/contract-simulator" element={<ContractSimulator />} />
            <Route path="/documents" element={<DocumentsExplorer />} />
            <Route path="/clause-explorer" element={<ClauseExplorer />} />
            <Route path="/clause-drift" element={<ClauseDrift />} />
            <Route path="/clause-library" element={<ClauseLibrary />} />
            <Route path="/anomalies" element={<Anomalies />} />
            <Route path="/model-accuracy" element={<ModelAccuracy />} />
            <Route path="/saved-views" element={<SavedViews />} />
            <Route path="/usage" element={<RateUsage />} />
            <Route path="/search" element={<AdvancedSearch />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/plugins" element={<Plugins />} />
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/developers" element={<DeveloperPortal />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/partners" element={<PartnerOnboarding />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/demo-mode" element={<DemoMode />} />
            <Route element={<RoleProtectedRoute roles={["ROLE_ADMIN"]} />}>
              <Route path="/billing" element={<Billing />} />
              <Route path="/users" element={<Users />} />
              <Route path="/workspaces" element={<Workspaces />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/flags" element={<FlagsAdmin />} />
              <Route path="/apikeys" element={<ApiKeys />} />
              <Route path="/sessions" element={<SessionsAdmin />} />
              <Route path="/diagnostics" element={<Diagnostics />} />
              <Route path="/playbooks" element={<PersonaPlaybooks />} />
              <Route path="/clause-templates" element={<ClauseTemplates />} />
            </Route>
            <Route path="/workspaces" element={<Workspaces />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/webhooks" element={<WebhooksTester />} />
            <Route path="/whats-new" element={<WhatsNew />} />
            <Route path="/forbidden" element={<Forbidden />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </AuthProvider>
  )
}
